/**
 * Progress Indicator Component
 * Shows current step in booking flow
 */

import { WidgetView } from '@/store/widgetStore';

interface Step {
  id: WidgetView;
  name: string;
  description: string;
}

const BOOKING_STEPS: Step[] = [
  {
    id: WidgetView.PROVIDER_SELECTION,
    name: 'Select Provider',
    description: 'Choose your healthcare provider',
  },
  {
    id: WidgetView.SLOT_SELECTION,
    name: 'Choose Time',
    description: 'Select date and time',
  },
  {
    id: WidgetView.PATIENT_INFO,
    name: 'Your Information',
    description: 'Enter patient details',
  },
  {
    id: WidgetView.CONFIRMATION,
    name: 'Confirm',
    description: 'Review and confirm',
  },
];

export interface ProgressIndicatorProps {
  currentStep: WidgetView;
}

export function ProgressIndicator({ currentStep }: ProgressIndicatorProps) {
  const currentStepIndex = BOOKING_STEPS.findIndex((step) => step.id === currentStep);

  // Don't show progress for loading, error, or success states
  if (
    currentStep === WidgetView.LOADING ||
    currentStep === WidgetView.ERROR ||
    currentStep === WidgetView.SUCCESS
  ) {
    return null;
  }

  return (
    <nav aria-label="Progress" className="ww-mb-8">
      <ol className="ww-flex ww-items-center ww-justify-between">
        {BOOKING_STEPS.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;

          return (
            <li
              key={step.id}
              className={`ww-flex-1 ${index !== BOOKING_STEPS.length - 1 ? 'ww-pr-8 sm:ww-pr-20' : ''} ww-relative`}
            >
              {/* Progress Line */}
              {index !== BOOKING_STEPS.length - 1 && (
                <div
                  className="ww-absolute ww-top-4 ww-left-4 ww--ml-px ww-mt-0.5 ww-h-0.5 ww-w-full"
                  aria-hidden="true"
                >
                  <div
                    className={`ww-h-full ww-transition-all ww-duration-300 ${
                      isCompleted ? 'ww-bg-primary' : 'ww-bg-gray-200'
                    }`}
                  />
                </div>
              )}

              {/* Step Content */}
              <div className="ww-group ww-relative ww-flex ww-flex-col ww-items-start">
                <span className="ww-flex ww-items-center">
                  <span
                    className={`ww-relative ww-z-10 ww-flex ww-h-8 ww-w-8 ww-items-center ww-justify-center ww-rounded-full ww-transition-all ww-duration-300 ${
                      isCompleted
                        ? 'ww-bg-primary'
                        : isCurrent
                        ? 'ww-border-2 ww-border-primary ww-bg-white'
                        : 'ww-border-2 ww-border-gray-300 ww-bg-white'
                    }`}
                  >
                    {isCompleted ? (
                      <svg
                        className="ww-h-5 ww-w-5 ww-text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span
                        className={`ww-text-sm ww-font-semibold ${
                          isCurrent ? 'ww-text-primary' : 'ww-text-gray-500'
                        }`}
                      >
                        {index + 1}
                      </span>
                    )}
                  </span>
                </span>

                <span className="ww-mt-2 ww-flex ww-min-w-0 ww-flex-col">
                  <span
                    className={`ww-text-xs ww-font-semibold ww-uppercase ww-tracking-wide ${
                      isCurrent
                        ? 'ww-text-primary'
                        : isCompleted
                        ? 'ww-text-gray-900'
                        : 'ww-text-gray-500'
                    }`}
                  >
                    {step.name}
                  </span>
                  <span className="ww-text-xs ww-text-gray-500 ww-hidden sm:ww-block">
                    {step.description}
                  </span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
